import { lazy, type ComponentType } from "react";

const KEY = "cp.chunk-reload-at";

/** True for the errors browsers raise when a code-split file no longer exists (after a deploy). */
export function isChunkLoadError(err: unknown): boolean {
  const msg = String((err as Error)?.message ?? err);
  return /dynamically imported module|Importing a module script failed|error loading dynamically imported module|Failed to fetch dynamically|ChunkLoadError|MIME type/i.test(msg);
}

/**
 * After a deploy, an open tab may still reference the previous build's files.
 * Instead of crashing, reload once onto the new version. The guard prevents a
 * reload loop if something else is wrong.
 */
export function reloadOnceForNewVersion(): boolean {
  try {
    const last = Number(sessionStorage.getItem(KEY) ?? 0);
    if (Date.now() - last < 15_000) return false;
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch {
    /* storage blocked: still try once */
  }
  location.reload();
  return true;
}

export function lazyPage<T extends ComponentType<any>>(load: () => Promise<{ default: T }>) {
  return lazy(() =>
    load().catch((err: unknown) => {
      if (isChunkLoadError(err) && reloadOnceForNewVersion()) {
        // Keep Suspense showing its fallback while the page reloads.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }),
  );
}
